import React, { useContext, useState, useEffect } from 'react';
import { SiteContext, UserContext } from '../Contexts';
import { SiteDataContext } from '../Models/Models';
import { Utils } from '../Services/Utils';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';

export default function SideMenu() {
    const { currentUser }: any = useContext(UserContext);
    const { currentSite, setCurrentSite }: any = useContext(SiteContext);
    const navigate = useNavigate();

    const shouldPulse = !Utils.hasUserPickedWebsite(currentSite.id);
    const websites: SiteDataContext[] = currentUser?.websites ?? [];
    const [pulseIndex, setPulseIndex] = useState(0);

    useEffect(() => {
        if (!shouldPulse || websites.length === 0) return;
        const interval = setInterval(() => {
            setPulseIndex(prev => (prev + 1) % websites.length);
        }, 1200);
        return () => clearInterval(interval);
    }, [shouldPulse, websites.length]);

    const handlePick = (website: SiteDataContext) => {
        setCurrentSite({
            id: website.id,
            domainName: website.domainName,
            shortName: website.shortName,
            settings: website.settings,
        });
        navigate('/home', { replace: true });
    };

    return (
        <div style={{ padding: '0.75rem 0.6rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            <p className="st-eyebrow" style={{
                color: 'rgba(207,199,228,0.45)',
                margin: '0 0 0.5rem 0.3rem',
            }}>
                Weby
            </p>

            {websites.length > 0 ? (
                websites.map((site, idx) => {
                    const active = currentSite.id === site.id;
                    const isPulsing = shouldPulse && !active && idx === pulseIndex;
                    return (
                        <SiteButton
                            key={site.id}
                            label={Utils.capitalizeFirstLetter(site.shortName)}
                            active={active}
                            isPulsing={isPulsing}
                            onClick={() => handlePick(site)}
                        />
                    );
                })
            ) : (
                <p style={{ color: 'rgba(207,199,228,0.45)', fontSize: '0.75rem', textAlign: 'center', padding: '0.5rem', margin: 0 }}>
                    Žiadne stránky
                </p>
            )}
        </div>
    );
}

const SiteButton: React.FC<{
    label: string;
    active: boolean;
    isPulsing: boolean;
    onClick: () => void;
}> = ({ label, active, isPulsing, onClick }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <button
            onClick={onClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            title={label}
            style={{
                width: '100%',
                padding: '0.55rem 0.5rem',
                borderRadius: '8px',
                border: active ? '1px solid rgba(139,119,232,0.4)' : '1px solid transparent',
                backgroundColor: active
                    ? 'rgba(109,87,212,0.22)'
                    : hovered
                    ? 'rgba(255,255,255,0.06)'
                    : 'transparent',
                color: active ? '#F4F4F5' : hovered ? '#CFC7E4' : 'rgba(207,199,228,0.55)',
                fontFamily: 'var(--st-font-body)',
                fontWeight: active ? 700 : 500,
                fontSize: '0.78rem',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                lineHeight: 1.35,
                animation: isPulsing ? 'siteButtonPulse 1.2s ease-in-out infinite' : undefined,
            }}
        >
            <FontAwesomeIcon
                icon={faGlobe}
                style={{ fontSize: '0.62rem', flexShrink: 0, color: active ? '#8B77E8' : 'rgba(207,199,228,0.35)' }}
            />
            <span style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}>
                {label}
            </span>
        </button>
    );
};
